import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface AuditEntry {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string;
  created_at: string;
  admin_name: string;
}

export interface DashboardMetrics {
  // Companies
  totalCompanies: number;
  activeCompanies: number;
  suspendedCompanies: number;
  // Users
  totalUsers: number;
  activeUsers: number;
  suspendedUsers: number;
  deactivatedUsers: number;
  // Alerts
  pendingRequests: number;
  noAdminCompanies: number;
  // Catalog
  activeSystemItems: number;
  inactiveSystemItems: number;
  stale90180Items: number;
  stale180PlusItems: number;
  totalBrowseCategories: number;
  totalPlatformSuppliers: number;
  staleAmberDays: number;
  staleRedDays: number;
  // Recent activity
  recentAudit: AuditEntry[];
}

export const useDashboardMetrics = () => {
  const query = useQuery({
    queryKey: ["dashboard-metrics"],
    staleTime: 60_000,
    queryFn: async (): Promise<DashboardMetrics> => {
      const { data, error } = await supabase.rpc("get_dashboard_metrics" as any);
      if (error) throw error;
      const d = data as any;
      return {
        totalCompanies:         d.total_companies          ?? 0,
        activeCompanies:        d.active_companies         ?? 0,
        suspendedCompanies:     d.suspended_companies      ?? 0,
        totalUsers:             d.total_users              ?? 0,
        activeUsers:            d.active_users             ?? 0,
        suspendedUsers:         d.suspended_users          ?? 0,
        deactivatedUsers:       d.deactivated_users        ?? 0,
        pendingRequests:        d.pending_requests         ?? 0,
        noAdminCompanies:       d.no_admin_companies       ?? 0,
        activeSystemItems:      d.active_system_items      ?? 0,
        inactiveSystemItems:    d.inactive_system_items    ?? 0,
        stale90180Items:        d.stale_90_180_items       ?? 0,
        stale180PlusItems:      d.stale_180_plus_items     ?? 0,
        totalBrowseCategories:  d.total_browse_categories  ?? 0,
        totalPlatformSuppliers: d.total_platform_suppliers ?? 0,
        staleAmberDays:         d.stale_amber_days         ?? 90,
        staleRedDays:           d.stale_red_days           ?? 180,
        recentAudit:            d.recent_audit             ?? [],
      };
    },
  });

  return {
    metrics:     query.data,
    isLoading:   query.isLoading,
    error:       query.isError ? "Failed to load metrics." : null,
    lastUpdated: query.dataUpdatedAt ? new Date(query.dataUpdatedAt) : null,
    refetch:     query.refetch,
    isFetching:  query.isFetching,
  };
};
